import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ImagePlus, KeyRound, Loader2, LogOut, Upload as UploadIcon } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { useDirty } from '@/hooks/useDirty'
import { ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import {
  useDeleteAccount,
  useLeaveCurrentWorkspace,
  useProfile,
  useRequestDeleteCode,
  useUpdateName,
  useUploadImage,
} from '@/features/profile/queries'
import { useTenantSettings } from '@/features/company/queries'
import {
  useConfirmPasswordChange,
  useRequestPasswordChange,
} from '@/auth/useMemberships'
import { TwoFactorAuthCard } from '@/features/security/TwoFactorAuthCard'

const defaultAvatar = (name: string) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=0072CE&color=fff&size=128`

export function ProfilePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const profileQ = useProfile()
  const updateNameM = useUpdateName()
  const uploadM = useUploadImage()
  const requestCodeM = useRequestDeleteCode()
  const deleteAccountM = useDeleteAccount()
  const leaveWorkspaceM = useLeaveCurrentWorkspace()
  const requestPwdM = useRequestPasswordChange()
  const confirmPwdM = useConfirmPasswordChange()
  const tenantSettingsQ = useTenantSettings()
  const currentWorkspaceName =
    tenantSettingsQ.data?.tenant?.business_name ||
    tenantSettingsQ.data?.tenant?.name ||
    'this workspace'

  const [name, setName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [codeModalOpen, setCodeModalOpen] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)

  const [doneOpen, setDoneOpen] = useState(false)
  const [deletedWorkspaces, setDeletedWorkspaces] = useState<{ id: string; name: string }[]>([])

  // Leave-workspace flow state. Separate from delete-account so the two
  // confirmation paths can't get tangled. We require the user to type the
  // workspace name to avoid reflexive clicks; this matches the Company →
  // Danger Zone delete-workspace pattern.
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [leaveTyped, setLeaveTyped] = useState('')
  const [leaveError, setLeaveError] = useState<string | null>(null)
  const [leaveDoneOpen, setLeaveDoneOpen] = useState(false)
  const [leaveDoneMessage, setLeaveDoneMessage] = useState<string>('')

  // Change-password (email verification) state machine.
  //  'idle'    - show the "Send verification code" button
  //  'sent'    - show the code + newPassword form
  //  'saving'  - inflight
  const [pwdStep, setPwdStep] = useState<'idle' | 'sent' | 'saving'>('idle')
  const [pwdCode, setPwdCode] = useState('')
  const [pwdNew, setPwdNew] = useState('')
  const [pwdNewConfirm, setPwdNewConfirm] = useState('')
  const [pwdError, setPwdError] = useState<string | null>(null)

  useEffect(() => {
    if (profileQ.data && !selectedFile) setName(profileQ.data.name ?? '')
  }, [profileQ.data, selectedFile])

  const savedName = profileQ.data?.name ?? ''
  const nameDirty = useDirty(name, savedName)

  const handlePick = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onload = (e) => setPreviewUrl((e.target?.result as string) ?? null)
    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    try {
      await uploadM.mutateAsync(selectedFile)
      setSelectedFile(null)
      setPreviewUrl(null)
      if (fileRef.current) fileRef.current.value = ''
      toast({ title: 'Profile image updated successfully.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: messageFor(err, 'Unknown error'),
      })
    }
  }

  const handleSaveName = async () => {
    try {
      await updateNameM.mutateAsync(name.trim())
      toast({ title: 'Profile updated successfully.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to update profile',
        description: messageFor(err, ''),
      })
    }
  }

  const startDelete = async () => {
    setConfirmOpen(false)
    try {
      await requestCodeM.mutateAsync()
      setCode('')
      setCodeError(null)
      setCodeModalOpen(true)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: messageFor(err, 'Failed to request code.'),
      })
    }
  }

  const submitDelete = async () => {
    const trimmed = code.trim()
    if (!trimmed) {
      setCodeError('Please enter the code.')
      return
    }
    setCodeError(null)
    try {
      const res = await deleteAccountM.mutateAsync(trimmed)
      setDeletedWorkspaces(res.deletedWorkspaces ?? [])
      setCodeModalOpen(false)
      setDoneOpen(true)
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
        setCodeError(String((err.body as { error: unknown }).error) || 'Invalid code.')
      } else {
        setCodeError('Server error. Please try again.')
      }
    }
  }

  const finishDelete = () => {
    try {
      localStorage.clear()
    } catch {
      /* ignore */
    }
    tokenManager.clear()
    window.location.href = '/login'
    // fallback if same-origin nav throws
    navigate('/login', { replace: true })
  }

  const submitLeave = async () => {
    setLeaveError(null)
    if (leaveTyped.trim() !== currentWorkspaceName) {
      setLeaveError(`Type "${currentWorkspaceName}" exactly to confirm.`)
      return
    }
    try {
      const res = await leaveWorkspaceM.mutateAsync()
      setLeaveOpen(false)
      setLeaveTyped('')
      setLeaveDoneMessage(
        res.workspaceSoftDeleted
          ? `You have left "${res.tenantName}". Because you were the only member, the workspace has also been removed.`
          : `You have left "${res.tenantName}". The remaining members continue to have access.`,
      )
      setLeaveDoneOpen(true)
    } catch (err) {
      // Show the server's specific guard message - owner / last super_admin /
      // platform tenant / impersonation are all 4xx with `code` set, and
      // the user-facing copy already explains the next step.
      setLeaveError(messageFor(err, 'Could not leave the workspace.'))
    }
  }

  const finishLeave = () => {
    // Same teardown as delete: the App JWT we're holding is for a tenant we
    // no longer belong to. Clear it and bounce to /login so the multi-tenant
    // login fork can hand the user their remaining workspaces (or the
    // empty-state path if this was their last one).
    try {
      localStorage.clear()
    } catch {
      /* ignore */
    }
    tokenManager.clear()
    window.location.href = '/login'
    navigate('/login', { replace: true })
  }

  if (profileQ.isLoading) {
    return (
      <Page title="My Profile">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (profileQ.isError) {
    return (
      <Page title="My Profile">
        <Alert variant="destructive">
          <AlertDescription>Error loading profile data.</AlertDescription>
        </Alert>
      </Page>
    )
  }

  const profile = profileQ.data!
  const avatarSrc = previewUrl ?? profile.profile_image_url ?? defaultAvatar(profile.name)

  return (
    <Page title="My Profile" width="wide">
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-[1fr_2fr]">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Profile Photo</h3>
              <p className="text-xs text-muted-foreground">
                Your profile picture is visible to team members in your workspace.
              </p>
              <div className="flex flex-col items-start gap-3">
                <img
                  src={avatarSrc}
                  alt="Profile"
                  className="size-32 rounded-pill border object-cover"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement
                    img.src = defaultAvatar(profile.name)
                  }}
                />
                <Input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePick(e.currentTarget.files)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => fileRef.current?.click()}
                  >
                    <ImagePlus className="size-4" /> Choose image
                  </Button>
                  {selectedFile ? (
                    <Button type="button" onClick={handleUpload} disabled={uploadM.isPending}>
                      {uploadM.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <UploadIcon className="size-4" />
                      )}
                      {uploadM.isPending ? 'Uploading…' : 'Upload new'}
                    </Button>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold">Account Information</h3>
              <FormField
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                placeholder="Your Name"
              />
              <FormField
                label="Email Address"
                hint="Email cannot be changed."
                control={<Input value={profile.email} disabled readOnly />}
              />
              <div>
                <Button onClick={handleSaveName} disabled={!nameDirty || updateNameM.isPending}>
                  {updateNameM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Save changes
                </Button>
              </div>
            </section>
          </div>
        </CardContent>
      </Card>

      <TwoFactorAuthCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-5" /> Change password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            We'll email a 6-digit verification code to <span className="font-medium">{profile?.email ?? ''}</span>.
            Enter it with your new password below.
          </p>
          {pwdStep === 'idle' ? (
            <Button
              variant="outline"
              disabled={requestPwdM.isPending}
              onClick={async () => {
                setPwdError(null)
                try {
                  await requestPwdM.mutateAsync()
                  setPwdStep('sent')
                  toast({ title: 'Verification code sent', description: 'Check your inbox.' })
                } catch (err) {
                  setPwdError(messageFor(err, 'Could not send code.'))
                }
              }}
            >
              {requestPwdM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Send verification code
            </Button>
          ) : (
            <div className="space-y-3">
              <FormField
                label="Verification code"
                control={
                  <Input
                    value={pwdCode}
                    onChange={(e) => setPwdCode(e.currentTarget.value)}
                    maxLength={10}
                    placeholder="123456"
                    inputMode="numeric"
                    className="font-mono"
                  />
                }
              />
              <FormField
                label="New password"
                control={
                  <Input type="password" value={pwdNew} onChange={(e) => setPwdNew(e.currentTarget.value)} />
                }
              />
              <FormField
                label="Confirm new password"
                control={
                  <Input type="password" value={pwdNewConfirm} onChange={(e) => setPwdNewConfirm(e.currentTarget.value)} />
                }
              />
              {pwdError ? (
                <Alert variant="destructive"><AlertDescription>{pwdError}</AlertDescription></Alert>
              ) : null}
              <div className="flex gap-2">
                <Button
                  disabled={confirmPwdM.isPending}
                  onClick={async () => {
                    setPwdError(null)
                    if (!pwdCode.trim() || !pwdNew) {
                      setPwdError('Enter the code and a new password.')
                      return
                    }
                    if (pwdNew !== pwdNewConfirm) {
                      setPwdError('New passwords do not match.')
                      return
                    }
                    try {
                      await confirmPwdM.mutateAsync({ code: pwdCode.trim(), newPassword: pwdNew })
                      setPwdStep('idle')
                      setPwdCode('')
                      setPwdNew('')
                      setPwdNewConfirm('')
                      toast({ title: 'Password updated', description: 'Your password was changed successfully.' })
                    } catch (err) {
                      setPwdError(messageFor(err, 'Could not change password.'))
                    }
                  }}
                >
                  {confirmPwdM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Update password
                </Button>
                <Button
                  variant="ghost"
                  disabled={confirmPwdM.isPending}
                  onClick={() => {
                    setPwdStep('idle')
                    setPwdCode('')
                    setPwdNew('')
                    setPwdNewConfirm('')
                    setPwdError(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  disabled={requestPwdM.isPending}
                  onClick={async () => {
                    setPwdError(null)
                    try {
                      await requestPwdM.mutateAsync()
                      toast({ title: 'New code sent' })
                    } catch (err) {
                      setPwdError(messageFor(err, 'Could not resend code.'))
                    }
                  }}
                >
                  Resend code
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogOut className="size-5" /> Leave this workspace
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Remove yourself from <span className="font-medium text-foreground">{currentWorkspaceName}</span> only.
            Your account and any other workspaces you belong to stay intact. An admin can re-invite you later.
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              setLeaveTyped('')
              setLeaveError(null)
              setLeaveOpen(true)
            }}
          >
            Leave workspace
          </Button>
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Deleting your account is irreversible and removes you from <em>every</em> workspace you belong to.
            If you only want to leave this one workspace, use <span className="font-medium text-foreground">Leave workspace</span> above instead.
          </p>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            Delete account
          </Button>
        </CardContent>
      </Card>

      <Modal.Confirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Account?"
        confirmLabel="Yes, delete my account"
        confirmVariant="danger"
        body={
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Are you absolutely sure? This will permanently delete your account and all associated data.
            </p>
            <p>This action cannot be undone.</p>
          </div>
        }
        onConfirm={startDelete}
        busy={requestCodeM.isPending}
      />

      <Modal
        open={codeModalOpen}
        onOpenChange={setCodeModalOpen}
        title="Confirm Deletion"
        description="Verification code sent to your email."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCodeModalOpen(false)} disabled={deleteAccountM.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDelete} disabled={deleteAccountM.isPending}>
              {deleteAccountM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Confirm delete
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            maxLength={6}
            placeholder="123456"
            inputMode="numeric"
            className="text-center font-mono text-xl tracking-[10px]"
          />
          {codeError ? (
            <Alert variant="destructive">
              <AlertDescription>{codeError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>

      <Modal.Alert
        open={doneOpen}
        onOpenChange={(v) => {
          setDoneOpen(v)
          if (!v) finishDelete()
        }}
        title="Account Deleted"
        body={
          deletedWorkspaces.length === 0
            ? 'Your account has been deleted. Redirecting…'
            : `Your account has been deleted. ${
                deletedWorkspaces.length === 1
                  ? `The workspace "${deletedWorkspaces[0].name}" was also removed because you were its only member.`
                  : `${deletedWorkspaces.length} workspaces (${deletedWorkspaces
                      .map((w) => `"${w.name}"`)
                      .join(', ')}) were also removed because you were their only member.`
              } Redirecting…`
        }
        onClose={finishDelete}
      />

      <Modal
        open={leaveOpen}
        onOpenChange={(v) => {
          setLeaveOpen(v)
          if (!v) {
            setLeaveTyped('')
            setLeaveError(null)
          }
        }}
        title={`Leave "${currentWorkspaceName}"?`}
        description="You will lose access immediately. An admin can re-invite you later."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setLeaveOpen(false)}
              disabled={leaveWorkspaceM.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitLeave}
              disabled={leaveWorkspaceM.isPending || leaveTyped.trim() !== currentWorkspaceName}
            >
              {leaveWorkspaceM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Leave workspace
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Type <span className="font-medium text-foreground">&ldquo;{currentWorkspaceName}&rdquo;</span> to confirm.
            If other members remain, your work stays available to them. If you&apos;re the only member, the
            workspace will also be removed.
          </p>
          <Input
            value={leaveTyped}
            onChange={(e) => setLeaveTyped(e.currentTarget.value)}
            placeholder={currentWorkspaceName}
            autoFocus
          />
          {leaveError ? (
            <Alert variant="destructive">
              <AlertDescription>{leaveError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>

      <Modal.Alert
        open={leaveDoneOpen}
        onOpenChange={(v) => {
          setLeaveDoneOpen(v)
          if (!v) finishLeave()
        }}
        title="Workspace Left"
        body={`${leaveDoneMessage} Redirecting…`}
        onClose={finishLeave}
      />
    </Page>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
